'use client';

/**
 * useAstorStream Hook
 *
 * Enhanced SSE streaming hook for the Astor AI assistant. Handles
 * event types emitted by the Astor streaming endpoint:
 *
 * - `chunk` / `content` -> streamed text content
 * - `action` -> dispatches to AstorContext (navigate/highlight/spotlight/suggest)
 * - `approval_request` -> renders ToolApprovalCard in chat
 * - `action_result` -> success/failure notification + link to created resource
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import type { AstorAction, AstorSSEEventType, ExecuteAction, MatchLevel } from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * A chat message in the Astor conversation
 */
export interface AstorStreamMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

/**
 * An approval request derived from an SSE `approval_request` event
 */
export interface AstorApprovalRequest {
  actionId: string;
  title: string;
  preview: ExecuteAction['preview'];
  riskLevel: `${MatchLevel}`;
  expiresInSeconds: number;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
}

/**
 * Result of a completed action
 */
export interface AstorActionResult {
  actionId: string;
  success: boolean;
  message: string;
  resourceUrl?: string;
  resourceId?: string;
}

/**
 * SSE event payload (discriminated union by event type)
 */
export interface AstorSSEEvent {
  event: AstorSSEEventType;
  data: string; // JSON-encoded payload
}

/**
 * Options for the useAstorStream hook
 */
export interface UseAstorStreamOptions {
  /** Called when an action event is received (navigate, highlight, etc.) */
  onAction?: (action: AstorAction) => void;
  /** Called when an approval_request event is received */
  onApprovalRequest?: (request: AstorApprovalRequest) => void;
  /** Called when an action_result event is received */
  onActionResult?: (result: AstorActionResult) => void;
  /** Called for each streamed text chunk */
  onMessage?: (chunk: string) => void;
  /** Called on connection or parsing errors */
  onError?: (error: Error) => void;
  /** Returns an auth token to include in SSE requests */
  getAuthToken?: () => string | null;
  /** Portal identifier to include in SSE requests */
  portal?: string;
}

/**
 * Return value of the useAstorStream hook
 */
export interface UseAstorStreamReturn {
  /** Current conversation messages */
  messages: AstorStreamMessage[];
  /** Whether the stream is currently active */
  isStreaming: boolean;
  /** Pending approval requests (keyed by actionId) */
  approvalRequests: AstorApprovalRequest[];
  /** Action results received during the session */
  actionResults: AstorActionResult[];
  /** Send a message to the Astor endpoint (opens SSE stream for response) */
  sendMessage: (content: string) => void;
  /** Approve a pending action */
  approveAction: (actionId: string) => void;
  /** Cancel/reject a pending action */
  cancelAction: (actionId: string) => void;
  /** Close the SSE connection */
  disconnect: () => void;
  /** Clear all messages and state */
  clearMessages: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

const SSE_EVENT_TYPES: (AstorSSEEventType | 'done' | 'metadata')[] = [
  'chunk',
  'content',
  'done',
  'metadata',
  'action',
  'approval_request',
  'action_result',
  'sources',
];

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useAstorStream(endpoint: string, options: UseAstorStreamOptions = {}): UseAstorStreamReturn {
  // State
  const [messages, setMessages] = useState<AstorStreamMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [approvalRequests, setApprovalRequests] = useState<AstorApprovalRequest[]>([]);
  const [actionResults, setActionResults] = useState<AstorActionResult[]>([]);

  // Refs for EventSource and stable callback references
  const eventSourceRef = useRef<EventSource | null>(null);
  const currentAssistantIdRef = useRef<string | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  /**
   * Processes a parsed SSE event and dispatches to the appropriate handler
   */
  const processEvent = useCallback(
    (eventType: AstorSSEEventType | 'done' | 'metadata' | 'error', payload: Record<string, unknown>) => {
      switch (eventType) {
        case 'chunk':
        case 'content': {
          const text = (payload['content'] as string) ?? (payload['text'] as string) ?? '';
          if (text) {
            // Append to current assistant message
            setMessages((prev) => {
              const assistantId = currentAssistantIdRef.current;
              if (!assistantId) return prev;
              return prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + text } : m));
            });
            optionsRef.current.onMessage?.(text);
          }
          break;
        }

        case 'done': {
          // Stream complete — finalize assistant message and close EventSource
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)));
          }
          setIsStreaming(false);
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          break;
        }

        case 'metadata': {
          // Metadata event — informational (conversationId, agentName, etc.)
          break;
        }

        case 'error': {
          const errorMsg = (payload['error'] as string) ?? 'Stream error';
          optionsRef.current.onError?.(new Error(errorMsg));
          setIsStreaming(false);
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          break;
        }

        case 'action': {
          const action = payload as unknown as AstorAction;
          optionsRef.current.onAction?.(action);
          break;
        }

        case 'approval_request': {
          const request: AstorApprovalRequest = {
            actionId: (payload['actionId'] as string) ?? (payload['toolCallId'] as string) ?? generateId(),
            title: (payload['title'] as string) ?? (payload['toolName'] as string) ?? '',
            preview: (payload['preview'] as ExecuteAction['preview']) ?? {
              title: '',
              description: '',
              fields: [],
              warnings: [],
            },
            riskLevel: ((payload['riskLevel'] as string) ?? 'medium') as `${MatchLevel}`,
            expiresInSeconds: (payload['expiresInSeconds'] as number) ?? 300,
            status: 'pending',
          };
          setApprovalRequests((prev) => [...prev, request]);
          optionsRef.current.onApprovalRequest?.(request);
          break;
        }

        case 'action_result': {
          const result: AstorActionResult = {
            actionId: (payload['actionId'] as string) ?? '',
            success: (payload['success'] as boolean) ?? false,
            message: (payload['message'] as string) ?? '',
            resourceUrl: payload['resourceUrl'] as string | undefined,
            resourceId: payload['resourceId'] as string | undefined,
          };
          setActionResults((prev) => [...prev, result]);
          // Update corresponding approval request status
          setApprovalRequests((prev) =>
            prev.map((ar) =>
              ar.actionId === result.actionId
                ? { ...ar, status: result.success ? 'approved' : ('rejected' as const) }
                : ar,
            ),
          );
          optionsRef.current.onActionResult?.(result);
          break;
        }

        case 'sources': {
          // Sources event — informational, no direct UI action needed here
          break;
        }
      }
    },
    [],
  );

  /**
   * Sends a user message and opens an SSE stream for the response
   */
  const sendMessage = useCallback(
    (content: string) => {
      if (!content.trim()) return;
      if (!endpoint) {
        optionsRef.current.onError?.(new Error('Astor stream endpoint is not configured'));
        return;
      }

      // Close existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Add user message
      const userMessage: AstorStreamMessage = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      // Create placeholder for assistant message
      const assistantId = generateId();
      currentAssistantIdRef.current = assistantId;
      const assistantMessage: AstorStreamMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      // Build SSE URL with message as query parameter.
      // SECURITY NOTE: EventSource does not support custom headers, so auth tokens
      // must be passed as URL params. Mitigations: JWTs are short-lived (15min),
      // HTTPS is enforced in production, and server logs sanitize token params.
      // Long-term fix: migrate to WebSocket for header-based auth.
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('message', content.trim());
      const token = optionsRef.current.getAuthToken?.();
      if (token) url.searchParams.set('token', token);
      if (optionsRef.current.portal) url.searchParams.set('portal', optionsRef.current.portal);

      try {
        const es = new EventSource(url.toString());
        eventSourceRef.current = es;

        es.onmessage = (event: MessageEvent) => {
          try {
            const parsed = JSON.parse(event.data as string) as Record<string, unknown>;
            // API sends 'type' field (metadata/content/done/sources/error)
            const eventType = (parsed['type'] as string) ?? (parsed['event'] as string) ?? 'content';
            processEvent(eventType as AstorSSEEventType | 'done' | 'metadata' | 'error', parsed);
          } catch {
            // If not JSON, treat as raw text chunk
            processEvent('content', { content: event.data as string });
          }
        };

        // Named event listeners for typed SSE events
        // Note: 'error' is intentionally excluded — it's a reserved EventSource event type.
        // Connection errors are handled by es.onerror above. Server-sent error payloads
        // arrive via es.onmessage with type='error' in the JSON body.
        for (const eventType of SSE_EVENT_TYPES) {
          es.addEventListener(eventType, (event: Event) => {
            try {
              const msgEvent = event as MessageEvent;
              const parsed = JSON.parse(msgEvent.data as string) as Record<string, unknown>;
              processEvent(eventType, parsed);
            } catch (err) {
              optionsRef.current.onError?.(
                err instanceof Error ? err : new Error(`Failed to parse ${eventType} event`),
              );
            }
          });
        }

        es.onerror = () => {
          // When stream completes, the server closes the connection.
          // EventSource tries to reconnect (readyState = CONNECTING).
          // We must close it to prevent infinite reconnection loops.
          optionsRef.current.onError?.(new Error('SSE connection error'));
          setIsStreaming(false);
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)));
          es.close();
          eventSourceRef.current = null;
        };
      } catch (err) {
        setIsStreaming(false);
        optionsRef.current.onError?.(err instanceof Error ? err : new Error('Failed to create EventSource'));
      }
    },
    [endpoint, processEvent],
  );

  /**
   * Approve a pending action
   */
  const approveAction = useCallback(
    (actionId: string) => {
      if (!endpoint) {
        optionsRef.current.onError?.(new Error('Astor stream endpoint is not configured'));
        return;
      }
      setApprovalRequests((prev) =>
        prev.map((ar) => (ar.actionId === actionId ? { ...ar, status: 'approved' as const } : ar)),
      );
      // Notify the server of approval (fire-and-forget)
      try {
        const url = new URL(endpoint, window.location.origin);
        fetch(`${url.origin}${url.pathname}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ actionId, decision: 'approve' }),
        }).catch(() => {
          // Silently handle network errors — the UI state is already updated
        });
      } catch {
        // Fire-and-forget
      }
    },
    [endpoint],
  );

  /**
   * Cancel/reject a pending action
   */
  const cancelAction = useCallback(
    (actionId: string) => {
      if (!endpoint) {
        optionsRef.current.onError?.(new Error('Astor stream endpoint is not configured'));
        return;
      }
      setApprovalRequests((prev) =>
        prev.map((ar) => (ar.actionId === actionId ? { ...ar, status: 'rejected' as const } : ar)),
      );
      // Notify the server of cancellation (fire-and-forget)
      try {
        const url = new URL(endpoint, window.location.origin);
        fetch(`${url.origin}${url.pathname}/cancel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ actionId, decision: 'cancel' }),
        }).catch(() => {
          // Silently handle network errors
        });
      } catch {
        // Fire-and-forget
      }
    },
    [endpoint],
  );

  /**
   * Close the SSE connection
   */
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
    setMessages((prev) => prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)));
  }, []);

  /**
   * Clear all messages and state
   */
  const clearMessages = useCallback(() => {
    disconnect();
    setMessages([]);
    setApprovalRequests([]);
    setActionResults([]);
    currentAssistantIdRef.current = null;
  }, [disconnect]);

  return useMemo(
    () => ({
      messages,
      isStreaming,
      approvalRequests,
      actionResults,
      sendMessage,
      approveAction,
      cancelAction,
      disconnect,
      clearMessages,
    }),
    [
      messages,
      isStreaming,
      approvalRequests,
      actionResults,
      sendMessage,
      approveAction,
      cancelAction,
      disconnect,
      clearMessages,
    ],
  );
}

export default useAstorStream;
